import { parseAssignedGradeSplitIds, parseAssignedGrades } from './schoolRecords';

const GROUPING_MODES = new Set(['grades', 'grades_and_splits', 'splits_only']);

export function normalizeCarpoolLineGroupingMode(value) {
  return GROUPING_MODES.has(value) ? value : 'grades';
}

export function isActiveCarpoolLine(line) {
  return line?.is_active !== false && line?.is_active !== 0 && String(line?.is_active) !== '0';
}

export function isDefaultCarpoolLine(line) {
  return line?.is_default === true || line?.is_default === 1 || String(line?.is_default) === '1';
}

export function parseCarpoolLineGrades(line) {
  return parseAssignedGrades(line?.assigned_grades);
}

export function parseCarpoolLineGradeSplitIds(line) {
  return parseAssignedGradeSplitIds(line?.assigned_grade_split_ids);
}

export function lineSupportsGrade(line, grade) {
  if (grade === null || grade === undefined || grade === '') return false;

  const normalizedGrade = Number(grade);
  if (!Number.isInteger(normalizedGrade)) return false;

  return parseCarpoolLineGrades(line).includes(normalizedGrade);
}

export function lineSupportsGradeSplit(line, gradeSplitId) {
  if (gradeSplitId === null || gradeSplitId === undefined || gradeSplitId === '') return false;

  const normalizedSplitId = Number(gradeSplitId);
  if (!Number.isInteger(normalizedSplitId) || normalizedSplitId <= 0) return false;

  return parseCarpoolLineGradeSplitIds(line).includes(normalizedSplitId);
}

export function sortCarpoolLinesForFallback(lines = []) {
  return [...lines].sort((left, right) => {
    const defaultDiff = Number(isDefaultCarpoolLine(right)) - Number(isDefaultCarpoolLine(left));
    if (defaultDiff !== 0) return defaultDiff;

    const leftId = Number(left?.carpool_line_id);
    const rightId = Number(right?.carpool_line_id);
    if (Number.isFinite(leftId) && Number.isFinite(rightId) && leftId !== rightId) {
      return leftId - rightId;
    }

    return String(left?.name || '').localeCompare(String(right?.name || ''));
  });
}

export function getDefaultCarpoolLine(lines = []) {
  return sortCarpoolLinesForFallback(lines.filter(isActiveCarpoolLine))[0] || null;
}

export function getGradeCarpoolLine(student, lines = []) {
  return sortCarpoolLinesForFallback(
    lines.filter((line) => isActiveCarpoolLine(line) && lineSupportsGrade(line, student?.grade))
  )[0] || null;
}

export function getGradeSplitCarpoolLine(student, lines = []) {
  return sortCarpoolLinesForFallback(
    lines.filter((line) => isActiveCarpoolLine(line) && lineSupportsGradeSplit(line, student?.grade_split_id))
  )[0] || null;
}

export function getStudentGroupCarpoolLine(student, lines = [], groupingMode) {
  const mode = normalizeCarpoolLineGroupingMode(groupingMode || student?.student_grouping_mode);

  if (mode !== 'grades') {
    const splitLine = getGradeSplitCarpoolLine(student, lines);
    if (splitLine || mode === 'splits_only') return { line: splitLine, source: splitLine ? 'split' : 'none' };
  }

  const gradeLine = getGradeCarpoolLine(student, lines);
  return { line: gradeLine, source: gradeLine ? 'grade' : 'none' };
}

export function resolveStudentCarpoolLine({
  student = null,
  explicitAssignment = null,
  lines = [],
  groupingMode,
} = {}) {
  const activeLines = lines.filter(isActiveCarpoolLine);
  const lineById = new Map(activeLines.map((line) => [Number(line.carpool_line_id), line]));
  const explicitIsActive = explicitAssignment
    && explicitAssignment.is_active !== false
    && explicitAssignment.is_active !== 0
    && String(explicitAssignment.is_active) !== '0';

  if (explicitIsActive) {
    const explicitLine = lineById.get(Number(explicitAssignment.carpool_line_id));
    if (explicitLine) {
      return {
        line: explicitLine,
        source: 'explicit',
      };
    }
  }

  const groupedLine = getStudentGroupCarpoolLine(student, activeLines, groupingMode);
  if (groupedLine.line) {
    return {
      line: groupedLine.line,
      source: groupedLine.source,
    };
  }

  const defaultLine = getDefaultCarpoolLine(activeLines);
  return {
    line: defaultLine,
    source: defaultLine ? 'default' : 'none',
  };
}
